import { GpxIconFactory } from './GpxIconFactory';

export interface IWaypointDialogValues {
    name: string;
    description: string | null;
    typeId: number;
}

export type WaypointDialogResult =
    | { action: 'save'; values: IWaypointDialogValues }
    | { action: 'delete' }
    | { action: 'cancel' };

/**
 * The in-map waypoint create/edit dialog (#gpxWaypointDialog). Promise-based:
 * show() resolves with the user's choice. Lives inside #map so it works in
 * full-screen mode.
 */
export class GpxWaypointDialog {

    private dialog: HTMLElement;
    private resolve: ((result: WaypointDialogResult) => void) | null = null;

    public constructor(private iconFactory: GpxIconFactory) {
        this.dialog = document.getElementById('gpxWaypointDialog');

        document.getElementById('gpxWaypointSave').addEventListener('click', () => this.handleSave());
        document.getElementById('gpxWaypointDelete').addEventListener('click', () => this.close({ action: 'delete' }));
        document.getElementById('gpxWaypointCancel').addEventListener('click', () => this.close({ action: 'cancel' }));
        document.getElementById('gpxWaypointCancelX').addEventListener('click', () => this.close({ action: 'cancel' }));

        document.getElementById('gpxWaypointType').addEventListener('change', () => this.updatePreview());
    }

    public show(values: IWaypointDialogValues | null, isExisting: boolean): Promise<WaypointDialogResult> {
        this.populateTypes(values?.typeId);

        (<HTMLInputElement>document.getElementById('gpxWaypointName')).value = values?.name ?? '';
        (<HTMLTextAreaElement>document.getElementById('gpxWaypointDesc')).value = values?.description ?? '';
        document.getElementById('gpxWaypointDialogTitle').textContent = isExisting ? 'Edit waypoint' : 'Add waypoint';
        document.getElementById('gpxWaypointDelete').classList.toggle('d-none', !isExisting);
        document.getElementById('gpxWaypointNameError').classList.add('d-none');

        this.updatePreview();
        this.dialog.classList.remove('d-none');
        (<HTMLInputElement>document.getElementById('gpxWaypointName')).focus();

        return new Promise<WaypointDialogResult>((resolve) => {
            this.resolve = resolve;
        });
    }

    get isOpen(): boolean {
        return !this.dialog.classList.contains('d-none');
    }

    public hide(): void {
        this.close({ action: 'cancel' });
    }

    private populateTypes(selectedTypeId?: number): void {
        const select = <HTMLSelectElement>document.getElementById('gpxWaypointType');
        select.innerHTML = '';
        for (const type of this.iconFactory.getAllTypes()) {
            const option = document.createElement('option');
            option.value = type.id.toString();
            option.textContent = type.name;
            if (selectedTypeId === type.id) {
                option.selected = true;
            }
            select.appendChild(option);
        }
    }

    private updatePreview(): void {
        const select = <HTMLSelectElement>document.getElementById('gpxWaypointType');
        const preview = <HTMLImageElement>document.getElementById('gpxWaypointPreview');
        const typeId = parseInt(select.value, 10);
        const uri = this.iconFactory.getMarkerDataUri(typeId);
        if (uri) {
            preview.src = uri;
        }
    }

    private handleSave(): void {
        const name = (<HTMLInputElement>document.getElementById('gpxWaypointName')).value.trim();
        if (!name) {
            document.getElementById('gpxWaypointNameError').classList.remove('d-none');
            return;
        }

        const description = (<HTMLTextAreaElement>document.getElementById('gpxWaypointDesc')).value.trim();
        const typeId = parseInt((<HTMLSelectElement>document.getElementById('gpxWaypointType')).value, 10);

        this.close({
            action: 'save',
            values: {
                name: name,
                description: description || null,
                typeId: typeId
            }
        });
    }

    private close(result: WaypointDialogResult): void {
        this.dialog.classList.add('d-none');
        const resolve = this.resolve;
        this.resolve = null;
        resolve?.(result);
    }
}
